/**
 * Rating Algorithm - Core ocean conditions calculation
 *
 * SYNC: This module is shared between frontend and backend.
 */

import { THRESHOLDS } from './thresholds.mjs';
import {
    parseWindSpeed,
    isInSwellWindow,
    isOffshoreWind,
    matchesIdealWind,
    matchesIdealTide,
    isInIdealSwellRange,
    getWaveCoefficient
} from './helpers.mjs';
import { parseNOAATimestamp, now } from '../time/index.mjs';

/**
 * Get skill level category from wave height
 * @param {number|null} waveHeight
 * @returns {string|null} 'beginner'|'novice'|'intermediate'|'advanced'|'expert'|null
 */
function getWaveSkillLevel(waveHeight) {
    if (waveHeight === null || waveHeight === undefined || isNaN(waveHeight)) {
        return null;
    }
    if (waveHeight < THRESHOLDS.skill.beginner) return 'beginner';
    if (waveHeight < THRESHOLDS.skill.novice) return 'novice';
    if (waveHeight < THRESHOLDS.skill.intermediate) return 'intermediate';
    if (waveHeight < THRESHOLDS.skill.advanced) return 'advanced';
    return 'expert';
}

const SKILL_MESSAGES = {
    beginner: 'Beginners welcome - ideal for learning',
    novice: 'Novice to intermediate surfers',
    intermediate: 'Intermediate to advanced surfers',
    advanced: 'Advanced surfers only',
    expert: 'Expert conditions - high risk'
};

/**
 * Calculate overall ocean conditions rating
 * @param {Object} weather - Weather data from NWS
 * @param {Array} predictions - Tide predictions
 * @param {Object} buoyData - Buoy data (wave height, period, direction, temp)
 * @param {Object} waterLevel - Current water level
 * @param {Object} spotConfig - Optional spot-specific configuration
 * @returns {Object} Rating result with score, hazards, details, and breakdown
 */
export function calculateOceanRating(weather, predictions, buoyData, waterLevel, spotConfig = null) {
    let score = 100;
    const hazards = [];
    const details = {
        wind: { value: 'Checking...', rating: 'unknown', penalty: 0, bonus: 0 },
        swell: { value: 'Checking...', rating: 'unknown', penalty: 0, bonus: 0 },
        tide: { value: 'Checking...', rating: 'unknown', penalty: 0, bonus: 0 },
        temp: { value: 'N/A', rating: 'unknown', penalty: 0, bonus: 0 }
    };

    // Track scoring breakdown for transparency
    const breakdown = {
        startingScore: 100,
        windPenalty: 0,
        swellPenalty: 0,
        tidePenalty: 0,
        hazardPenalty: 0,
        swellWindowPenalty: 0,
        skillMismatchPenalty: 0,
        idealWindBonus: 0,
        idealTideBonus: 0,
        idealSwellBonus: 0,
        finalScore: 0,
        factors: []
    };

    // WIND SCORING
    if (weather) {
        const windSpeed = parseWindSpeed(weather.windSpeed);
        const windDir = weather.windDirection;
        const isOffshore = isOffshoreWind(windDir, spotConfig);

        if (windSpeed !== null) {
            let windPenalty = 0;
            if (windSpeed > THRESHOLDS.wind.dangerous) {
                windPenalty = 30;
                hazards.push('Strong winds');
                details.wind = { value: `${windSpeed} mph ${windDir}`, rating: 'poor', penalty: windPenalty, bonus: 0 };
                breakdown.factors.push({ factor: 'Wind', detail: `${windSpeed} mph (dangerous)`, impact: -30 });
            } else if (windSpeed > THRESHOLDS.wind.strong) {
                windPenalty = 20;
                details.wind = { value: `${windSpeed} mph ${windDir}`, rating: 'poor', penalty: windPenalty, bonus: 0 };
                breakdown.factors.push({ factor: 'Wind', detail: `${windSpeed} mph (strong)`, impact: -20 });
            } else if (windSpeed > THRESHOLDS.wind.moderate) {
                windPenalty = isOffshore ? 5 : 15;
                details.wind = { value: `${Math.round(windSpeed)} mph ${windDir}`, rating: isOffshore ? 'fair' : 'poor', penalty: windPenalty, bonus: 0 };
                breakdown.factors.push({ factor: 'Wind', detail: `${Math.round(windSpeed)} mph ${isOffshore ? 'offshore' : 'onshore'}`, impact: -windPenalty });
            } else if (windSpeed > THRESHOLDS.wind.light) {
                windPenalty = isOffshore ? 0 : 5;
                details.wind = { value: `${Math.round(windSpeed)} mph ${windDir}`, rating: 'good', penalty: windPenalty, bonus: 0 };
                if (windPenalty > 0) {
                    breakdown.factors.push({ factor: 'Wind', detail: `${Math.round(windSpeed)} mph onshore`, impact: -windPenalty });
                } else {
                    breakdown.factors.push({ factor: 'Wind', detail: `${Math.round(windSpeed)} mph offshore`, impact: 0 });
                }
            } else {
                details.wind = { value: `Light ${windDir || ''}`, rating: 'good', penalty: 0, bonus: 0 };
                breakdown.factors.push({ factor: 'Wind', detail: 'Light winds', impact: 0 });
            }
            score -= windPenalty;
            breakdown.windPenalty = windPenalty;

            // Check for ideal wind bonus (spot-specific)
            if (spotConfig && matchesIdealWind(windDir, spotConfig.ideal_wind)) {
                const bonus = THRESHOLDS.bonus?.idealWind || 5;
                details.wind.bonus = bonus;
                breakdown.idealWindBonus = bonus;
                breakdown.factors.push({ factor: 'Ideal Wind', detail: `${windDir} matches spot preference`, impact: `+${bonus}` });
            }
        }

        // Weather hazards
        const forecast = (weather.shortForecast || '').toLowerCase();
        if (forecast.includes('thunderstorm') || forecast.includes('lightning')) {
            score -= 25;
            breakdown.hazardPenalty += 25;
            hazards.push('Lightning risk - Stay out of water');
            breakdown.factors.push({ factor: 'Hazard', detail: 'Lightning/Thunderstorm', impact: -25 });
        }
        if (forecast.includes('fog')) {
            score -= 10;
            breakdown.hazardPenalty += 10;
            hazards.push('Low visibility');
            breakdown.factors.push({ factor: 'Hazard', detail: 'Fog', impact: -10 });
        }
        if (forecast.includes('rain') && !forecast.includes('thunderstorm')) {
            score -= 5;
            breakdown.hazardPenalty += 5;
            breakdown.factors.push({ factor: 'Weather', detail: 'Rain', impact: -5 });
        }
    }

    // SWELL SCORING
    if (buoyData && buoyData.waveHeight !== null) {
        // Apply wave height coefficient if available from spot config
        const waveCoefficient = getWaveCoefficient(spotConfig);
        const rawHeight = buoyData.waveHeight;
        const height = rawHeight * waveCoefficient;
        const period = buoyData.wavePeriod;
        const direction = buoyData.waveDirection;
        let swellPenalty = 0;

        // Log coefficient if not 1.0
        if (waveCoefficient !== 1.0) {
            breakdown.factors.push({
                factor: 'Wave Coefficient',
                detail: `Buoy: ${rawHeight.toFixed(1)}ft × ${waveCoefficient} = ${height.toFixed(1)}ft at spot`,
                impact: 0
            });
        }

        // Check swell direction window (spot-specific)
        if (spotConfig && spotConfig.swell_window_min !== undefined) {
            const inWindow = isInSwellWindow(direction, spotConfig.swell_window_min, spotConfig.swell_window_max);
            if (!inWindow) {
                const penalty = THRESHOLDS.spotPenalty?.swellOutOfWindow || 15;
                swellPenalty += penalty;
                breakdown.swellWindowPenalty = penalty;
                breakdown.factors.push({
                    factor: 'Swell Direction',
                    detail: `${direction}° outside window (${spotConfig.swell_window_min}°-${spotConfig.swell_window_max}°)`,
                    impact: -penalty
                });
            }
        }

        // Period quality
        if (period !== null) {
            if (period >= THRESHOLDS.period.excellent) {
                details.swell.rating = 'good';
                breakdown.factors.push({ factor: 'Period', detail: `${period}s (excellent groundswell)`, impact: 0 });
            } else if (period >= THRESHOLDS.period.good) {
                swellPenalty += 5;
                details.swell.rating = 'good';
                breakdown.factors.push({ factor: 'Period', detail: `${period}s (good)`, impact: -5 });
            } else if (period >= THRESHOLDS.period.fair) {
                swellPenalty += 10;
                details.swell.rating = 'fair';
                breakdown.factors.push({ factor: 'Period', detail: `${period}s (fair)`, impact: -10 });
            } else {
                swellPenalty += 20;
                details.swell.rating = 'poor';
                breakdown.factors.push({ factor: 'Period', detail: `${period}s (wind chop)`, impact: -20 });
            }
        }

        // Height assessment
        if (height > THRESHOLDS.wave.dangerous) {
            swellPenalty += 20;
            hazards.push('Large dangerous surf');
            details.swell.rating = 'poor';
            breakdown.factors.push({ factor: 'Wave Height', detail: `${height.toFixed(1)}ft (dangerous)`, impact: -20 });
        } else if (height > THRESHOLDS.wave.large) {
            swellPenalty += 15;
            hazards.push('Large surf');
            details.swell.rating = 'fair';
            breakdown.factors.push({ factor: 'Wave Height', detail: `${height.toFixed(1)}ft (large)`, impact: -15 });
        } else if (height < THRESHOLDS.wave.flat) {
            swellPenalty += 10;
            details.swell.rating = 'fair';
            breakdown.factors.push({ factor: 'Wave Height', detail: `${height.toFixed(1)}ft (flat)`, impact: -10 });
        } else {
            breakdown.factors.push({ factor: 'Wave Height', detail: `${height.toFixed(1)}ft (ideal range)`, impact: 0 });
        }

        // Skill-appropriate adjustments (spot-specific)
        if (spotConfig && spotConfig.skill_level) {
            if (spotConfig.skill_level === 'beginner' && height > 4) {
                const penalty = THRESHOLDS.spotPenalty?.tooLargeForBeginner || 10;
                swellPenalty += penalty;
                breakdown.skillMismatchPenalty = penalty;
                breakdown.factors.push({ factor: 'Skill Match', detail: `Too large for beginner spot`, impact: -penalty });
            } else if ((spotConfig.skill_level === 'advanced' || spotConfig.skill_level === 'expert') && height < 2) {
                const penalty = THRESHOLDS.spotPenalty?.tooSmallForAdvanced || 5;
                swellPenalty += penalty;
                breakdown.skillMismatchPenalty = penalty;
                breakdown.factors.push({ factor: 'Skill Match', detail: `Too small for advanced spot`, impact: -penalty });
            }
        }

        score -= swellPenalty;
        breakdown.swellPenalty = swellPenalty;
        details.swell.penalty = swellPenalty;

        const heightStr = (height !== null && !isNaN(height)) ? height.toFixed(1) : '—';
        const periodStr = (period !== null && !isNaN(period)) ? period + 's' : '—';
        details.swell.value = `${heightStr} ft @ ${periodStr}`;

        // Check for ideal swell range bonus (spot-specific)
        if (spotConfig && isInIdealSwellRange(height, spotConfig.ideal_swell_min, spotConfig.ideal_swell_max)) {
            const bonus = THRESHOLDS.bonus?.idealSwellRange || 5;
            details.swell.bonus = bonus;
            breakdown.idealSwellBonus = bonus;
            breakdown.factors.push({
                factor: 'Ideal Swell',
                detail: `${height.toFixed(1)}ft in ideal range (${spotConfig.ideal_swell_min}-${spotConfig.ideal_swell_max}ft)`,
                impact: `+${bonus}`
            });
        }

        // Water temp
        if (buoyData.waterTemp !== null && !isNaN(buoyData.waterTemp)) {
            details.temp.value = `${Math.round(buoyData.waterTemp)}°F`;
            details.temp.rating = buoyData.waterTemp >= THRESHOLDS.water.comfortable ? 'good' : 'fair';
            if (buoyData.waterTemp < THRESHOLDS.water.cold) {
                hazards.push('Cold water - wetsuit required');
            }
        }
    } else {
        score -= 10; // Unknown swell penalty
        breakdown.swellPenalty = 10;
        breakdown.factors.push({ factor: 'Swell', detail: 'Data unavailable', impact: -10 });
    }

    // TIDE SCORING
    if (predictions && predictions.length > 0) {
        const currentTime = now();
        const nextTide = predictions.find(p => parseNOAATimestamp(p.time, false) > currentTime);

        if (nextTide) {
            const isRising = nextTide.type === 'H';
            const currentLevel = waterLevel ? waterLevel.value : null;
            let tidePenalty = 0;

            // Check for extreme tides
            if (currentLevel !== null) {
                if (currentLevel < THRESHOLDS.tide.veryLow) {
                    tidePenalty = 15;
                    details.tide = { value: 'Very low', rating: 'poor', penalty: tidePenalty, bonus: 0 };
                    hazards.push('Extreme low tide - shallow water hazards');
                    breakdown.factors.push({ factor: 'Tide', detail: `${currentLevel.toFixed(1)}ft (extreme low)`, impact: -15 });
                } else if (currentLevel > THRESHOLDS.tide.veryHigh) {
                    tidePenalty = 10;
                    details.tide = { value: 'Very high', rating: 'fair', penalty: tidePenalty, bonus: 0 };
                    breakdown.factors.push({ factor: 'Tide', detail: `${currentLevel.toFixed(1)}ft (very high)`, impact: -10 });
                } else if (currentLevel > THRESHOLDS.tide.low && currentLevel < THRESHOLDS.tide.high) {
                    details.tide = { value: `${isRising ? 'Rising' : 'Falling'}`, rating: 'good', penalty: 0, bonus: 0 };
                    breakdown.factors.push({ factor: 'Tide', detail: `${currentLevel.toFixed(1)}ft (mid-tide)`, impact: 0 });
                } else {
                    tidePenalty = 5;
                    details.tide = { value: `${isRising ? 'Rising' : 'Falling'}`, rating: 'fair', penalty: tidePenalty, bonus: 0 };
                    breakdown.factors.push({ factor: 'Tide', detail: `${currentLevel.toFixed(1)}ft (low/high)`, impact: -5 });
                }

                score -= tidePenalty;
                breakdown.tidePenalty = tidePenalty;

                // Check for ideal tide bonus (spot-specific)
                if (spotConfig && matchesIdealTide(currentLevel, spotConfig.ideal_tide)) {
                    const bonus = THRESHOLDS.bonus?.idealTide || 5;
                    details.tide.bonus = bonus;
                    breakdown.idealTideBonus = bonus;
                    breakdown.factors.push({ factor: 'Ideal Tide', detail: `Matches spot preference (${spotConfig.ideal_tide})`, impact: `+${bonus}` });
                }
            } else {
                details.tide = { value: isRising ? 'Rising' : 'Falling', rating: 'fair', penalty: 0, bonus: 0 };
            }
        }
    }

    // Apply bonuses (capped at 100)
    const totalBonus = breakdown.idealWindBonus + breakdown.idealTideBonus + breakdown.idealSwellBonus;
    score = Math.min(100, Math.max(0, score + totalBonus));
    breakdown.finalScore = score;

    // Check if all primary data sources are missing
    const hasNoWeather = !weather;
    const hasNoBuoyData = !buoyData || buoyData.waveHeight === null || buoyData.waveHeight === undefined;
    const hasNoPredictions = !predictions || predictions.length === 0;
    const allDataMissing = hasNoWeather && hasNoBuoyData && hasNoPredictions;

    // Calculate final rating
    let rating, color, message, icon;

    // If all data is missing, show LIMITED instead of misleading rating
    if (allDataMissing) {
        rating = 'LIMITED';
        color = '#9e9e9e'; // Gray
        icon = '\u26A0'; // Warning sign (Unicode)
        message = 'Insufficient data - check conditions in person';
        score = null; // Don't show misleading score
    } else if (hazards.some(h => h.includes('Lightning') || h.includes('Strong winds') || h.includes('Large dangerous'))) {
        rating = 'HAZARDOUS';
        color = '#ef5350';
        icon = '\u26A0'; // Warning sign (Unicode)
        message = 'Dangerous conditions - exercise extreme caution';
    } else if (score >= THRESHOLDS.rating.good) {
        rating = 'GOOD';
        color = '#00d4aa';
        icon = '\uD83C\uDF0A'; // Wave emoji (Unicode)
        message = 'Great conditions for surfing and ocean activities';
    } else if (score >= THRESHOLDS.rating.fair) {
        rating = 'FAIR';
        color = '#ffb74d';
        icon = '\uD83C\uDF0A'; // Wave emoji (Unicode)
        message = 'Decent conditions - some factors less than ideal';
    } else {
        rating = 'POOR';
        color = '#ff8a65';
        icon = '\uD83C\uDF0A'; // Wave emoji (Unicode)
        message = 'Challenging conditions - experienced water users only';
    }

    // Compute context-aware skill recommendation
    let skillRecommendation;
    const waveSkill = getWaveSkillLevel(buoyData?.waveHeight);

    if (rating === 'HAZARDOUS') {
        skillRecommendation = 'Dangerous conditions - not recommended';
    } else if (rating === 'LIMITED') {
        skillRecommendation = 'Check conditions before entering water';
    } else if (rating === 'POOR' && waveSkill === 'beginner') {
        skillRecommendation = 'Conditions challenging despite small waves';
    } else {
        skillRecommendation = SKILL_MESSAGES[waveSkill] || 'Check conditions before entering water';
    }

    return {
        rating,
        score,
        color,
        message,
        icon,
        hazards,
        details,
        breakdown,
        skillRecommendation,
        waveHeight: buoyData ? buoyData.waveHeight : null,
        spotConfig: spotConfig ? {
            name: spotConfig.name,
            skill_level: spotConfig.skill_level,
            ideal_wind: spotConfig.ideal_wind,
            ideal_tide: spotConfig.ideal_tide,
            ideal_swell_min: spotConfig.ideal_swell_min,
            ideal_swell_max: spotConfig.ideal_swell_max
        } : null
    };
}

/**
 * Get skill level recommendation based on wave height only
 * Note: For context-aware recommendations that consider overall rating,
 * use the skillRecommendation property from calculateOceanRating() result.
 * @param {number|null} waveHeight - Wave height in feet
 * @returns {string}
 */
export function getSkillRecommendation(waveHeight) {
    const level = getWaveSkillLevel(waveHeight);
    return SKILL_MESSAGES[level] || 'Check conditions before entering water';
}

/**
 * Format the breakdown for display in UI
 * @param {Object} breakdown - Breakdown object from calculateOceanRating
 * @returns {Array}
 */
export function formatBreakdownForDisplay(breakdown) {
    if (!breakdown || !breakdown.factors) return [];

    return breakdown.factors.map(f => ({
        factor: f.factor,
        detail: f.detail,
        impact: typeof f.impact === 'number' ? (f.impact === 0 ? '—' : f.impact.toString()) : f.impact
    }));
}
